/**
 * Pure view model for the `/pools?poolId=` pool screen.
 *
 * This module is pure: no React import, no DOM, no fetch. Every server-owned
 * string (a rules preamble, a chat preview) is rendered verbatim per decision
 * C18: this module never computes, formats or branches on a contest rule.
 *
 * ISSUE-952 turned the pool home card stack into a screen with four tabs, so
 * the Requests, Members and More card copy this module used to own went with
 * the cards. What is left is the phase machine, the header model and the Chat
 * card's own state, which the new screen still reads.
 *
 * A `FORBIDDEN_POOL` read resolves to `noAccess`, not `failed`, per the Q1
 * part B decision from ISSUE-512's open-questions record (dated 2026-08-22).
 * `apps/app/src/errors/poolAccess.ts` is the shared definition ISSUE-512's
 * settings pane uses too, so the two screens cannot disagree.
 */
import { isPoolNoAccessCode } from "../../errors/poolAccess";
import { buildPreviewLine } from "../messages/messagePreviewLine";
import { memberActionConfirm } from "../poolMembers/memberActionConfirm";
import {
  contestTypeLabel,
  leagueShortLabel,
  memberCountLabel,
  poolMarkInitials,
  poolMarkTone,
  type typePoolMarkTone,
} from "../pools/poolsListState";

export const POOL_HOME_LOADING_LABEL = "Loading this pool";
export const POOL_HOME_FAILED_MESSAGE = "We could not load this pool.";
export const POOL_HOME_RETRY_LABEL = "Try again";
export const RULES_CARD_TITLE = "Rules";
export const RULES_SHOW_MORE_LABEL = "Show more";
export const RULES_SHOW_LESS_LABEL = "Show less";
export const CHAT_CARD_TITLE = "Chat";
export const CHAT_OFF_BODY = "Chat is off";
export const CHAT_OFF_NOTE = "History is still here to read";
export const CHAT_OFF_BADGE = "Read-only";
export const CHAT_NO_MESSAGES_BODY = "No messages yet";
/**
 * The Chat card while the pool's chat is still being set up: chat is on for
 * this pool, the fan's chat list has answered, and there is no chat in it for
 * this pool. That is the truthful state of a pool whose chat provisioning is
 * still in the worker's queue (pool-api enqueues it off this very read, see
 * services/pool-api/src/lib/healPoolChatOnRead.ts) or whose roster row for
 * this fan has not landed yet. It used to read "No messages yet" with no
 * button, which described a chat that did not exist as if it were empty.
 * Plain, no exclamation, no dash characters, per the `human-writing` rule.
 */
export const CHAT_PENDING_BODY = "Setting up the chat";
export const CHAT_PENDING_NOTE = "It will be ready in a moment";
/**
 * The pool chat's entry point. New string, no design-brief line to quote: the
 * Chat card shipped read-only and the brief never named a label for opening
 * it, because nothing here opened anything. Plain, no exclamation, no dash
 * characters, per the `human-writing` rule. It reads the same in both card
 * variants, including the switched-off one, where CHAT_OFF_NOTE already
 * promises the history is still there to read.
 */
export const CHAT_OPEN_LABEL = "Open chat";
export const NOTIFICATIONS_CARD_TITLE = "Notifications";
export const NOTIFICATIONS_FOLLOWING_GLOBAL = "Following your global settings";
export const NOTIFICATIONS_CUSTOM = "Custom for this pool";
/**
 * The leave-pool confirm, derived from the one member-action confirm table
 * rather than written again here (ISSUE-817 R3).
 *
 * These three used to be their own strings, and the body was
 * "You'll lose your entry and your picks.", which is not what the design pack
 * says (`26-pool-members.md`, ISSUE-195 R5) and not what `apps/mobile` shows.
 * A fan who leaves a pool on their phone and on the web was told two different
 * things about the same button. The names stay because two call sites import
 * them; only the source of the strings changed.
 */
const LEAVE_POOL_CONFIRM = memberActionConfirm({
  actionId: "leavePool",
  targetDisplayName: "",
});

export const LEAVE_POOL_LABEL =
  LEAVE_POOL_CONFIRM?.confirmLabel ?? "Leave pool";
export const LEAVE_POOL_CONFIRM_TITLE =
  LEAVE_POOL_CONFIRM?.title ?? "Leave this pool?";
export const LEAVE_POOL_CONFIRM_BODY = LEAVE_POOL_CONFIRM?.body ?? "";

export type typePoolRole = "owner" | "admin" | "member";

export type typePoolHomeHeaderModel = {
  poolId: string;
  name: string;
  /** Two uppercase letters, or "?" for an unnamed pool. Same helper PoolCard uses. */
  initials: string;
  markTone: typePoolMarkTone;
  leagueLabel: string; // "NFL"
  contestLabel: string; // "Survivor"
  membersLabel: string; // "14 members"
  seasonLabel: string; // "2026 season"
};

export type typePoolHomeState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "noAccess" }
  | {
      phase: "ready";
      header: typePoolHomeHeaderModel;
      viewerPoolRole: typePoolRole;
      pendingMemberCount: number;
      chatEnabled: boolean;
      commissionerPreamble: string | null;
      /** False for an owner: /pool/leave answers CONFLICT ownerMustTransfer. */
      canLeave: boolean;
      /**
       * ISSUE-1280. Where play stands in this pool for this fan, the same
       * line the pool card shows ("Ready to play", "Grid open · 3 of 5
       * squares", "The contest has ended"). Server-owned and rendered
       * verbatim (C18). null while pool-api has none to give.
       */
      statusLine: string | null;
    };

/**
 * `chatId` is on the `off` and `on` variants, and it is what says whether
 * the card can be opened at all: no id, so no entry point, rather than a
 * button that navigates to a conversation that is not there. Switching a
 * pool's chat off does not take its history away (`CHAT_OFF_NOTE`), so the
 * off variant carries an id too.
 *
 * `pending` is the third state: chat is on, the fan's chat list has answered,
 * and this pool is not in it. The chat has not been provisioned yet, or this
 * fan's roster row has not landed, and either way the worker is on it. A
 * list still in flight is NOT pending: it resolves to `on` with no id and
 * the empty preview, exactly as before, so a pool with a chat never flashes
 * "Setting up the chat" on the way to its real preview.
 */
export type typeChatCardModel =
  | { variant: "off"; chatId: string | null }
  | { variant: "pending" }
  | {
      variant: "on";
      chatId: string | null;
      preview: string;
      unreadCount: number;
    };

export function toPoolHomeHeaderModel(input: {
  poolId: string;
  name: string;
  leagueKey: string;
  contestType: string;
  seasonYear: number;
  activeMemberCount: number;
}): typePoolHomeHeaderModel {
  return {
    poolId: input.poolId,
    name: input.name,
    initials: poolMarkInitials(input.name),
    markTone: poolMarkTone(input.poolId),
    leagueLabel: leagueShortLabel(input.leagueKey),
    contestLabel: contestTypeLabel(input.contestType),
    membersLabel: memberCountLabel(input.activeMemberCount),
    seasonLabel: `${input.seasonYear} season`,
  };
}

export function resolvePoolHomeState(input: {
  isLoading: boolean;
  hasFailed: boolean;
  errorCode?: string | null | undefined;
  pool: {
    poolId: string;
    name: string;
    leagueKey: string;
    contestType: string;
    seasonYear: number;
    chatEnabled: boolean;
    commissionerPreamble?: string | null;
  } | null;
  viewerPoolRole: typePoolRole | null;
  activeMemberCount: number;
  pendingMemberCount: number;
  statusLabel?: string | null | undefined;
}): typePoolHomeState {
  if (isPoolNoAccessCode(input.errorCode ?? null)) {
    return { phase: "noAccess" };
  }
  if (input.hasFailed) {
    return { phase: "failed" };
  }
  if (input.isLoading) {
    return { phase: "loading" };
  }
  if (input.pool === null || input.viewerPoolRole === null) {
    return { phase: "failed" };
  }
  return {
    phase: "ready",
    header: toPoolHomeHeaderModel({
      poolId: input.pool.poolId,
      name: input.pool.name,
      leagueKey: input.pool.leagueKey,
      contestType: input.pool.contestType,
      seasonYear: input.pool.seasonYear,
      activeMemberCount: input.activeMemberCount,
    }),
    viewerPoolRole: input.viewerPoolRole,
    pendingMemberCount: input.pendingMemberCount,
    chatEnabled: input.pool.chatEnabled,
    commissionerPreamble: rulesText(input.pool.commissionerPreamble),
    canLeave: input.viewerPoolRole !== "owner",
    statusLine:
      typeof input.statusLabel === "string" && input.statusLabel.trim() !== ""
        ? input.statusLabel.trim()
        : null,
  };
}

export function findPoolChat(input: {
  poolId: string;
  items:
    | readonly {
        chatId: string;
        chatType: string;
        poolId?: string | null;
        lastMessagePreview?: string | null;
        lastMessageSenderDisplayName?: string | null;
        lastMessageSenderIsSelf?: boolean;
        unreadCount: number;
      }[]
    | null;
}): {
  chatId: string;
  lastMessagePreview: string | null;
  lastMessageSenderDisplayName: string | null;
  lastMessageSenderIsSelf: boolean;
  unreadCount: number;
} | null {
  if (input.items === null) {
    return null;
  }
  const match = input.items.find(
    (item) => item.chatType === "pool" && item.poolId === input.poolId,
  );
  if (match === undefined) {
    return null;
  }
  return {
    chatId: match.chatId,
    lastMessagePreview: match.lastMessagePreview ?? null,
    lastMessageSenderDisplayName: match.lastMessageSenderDisplayName ?? null,
    lastMessageSenderIsSelf: match.lastMessageSenderIsSelf ?? false,
    unreadCount: match.unreadCount,
  };
}

export function resolveChatCard(input: {
  chatEnabled: boolean;
  chat: {
    chatId: string;
    lastMessagePreview: string | null;
    lastMessageSenderDisplayName: string | null;
    lastMessageSenderIsSelf: boolean;
    unreadCount: number;
  } | null;
  /**
   * True once the fan's chat list has answered. Only a loaded list with no
   * chat for this pool means the chat is still being set up; an unloaded
   * one means nothing yet.
   */
  isChatListLoaded: boolean;
}): typeChatCardModel {
  if (!input.chatEnabled) {
    return { variant: "off", chatId: input.chat?.chatId ?? null };
  }
  if (input.chat === null && input.isChatListLoaded) {
    return { variant: "pending" };
  }
  return {
    variant: "on",
    chatId: input.chat?.chatId ?? null,
    preview: buildPreviewLine({
      preview: input.chat?.lastMessagePreview ?? null,
      senderDisplayName: input.chat?.lastMessageSenderDisplayName ?? null,
      senderIsSelf: input.chat?.lastMessageSenderIsSelf ?? false,
      emptyLabel: CHAT_NO_MESSAGES_BODY,
    }),
    unreadCount: input.chat?.unreadCount ?? 0,
  };
}

/**
 * How long the chat list poll waits while the pool's chat is being set up.
 * Ten seconds is well inside the worker's first retry window and cheap for
 * one list read, and the poll only runs while the pending state is on screen.
 */
export const CHAT_PENDING_POLL_MS = 10_000;

/**
 * The refetch interval for the Chat card's list read, decided from the
 * cached answer: the poll is on only while chat is on, the list has answered,
 * and this pool is missing from it (the pending state). Everything else, a
 * chat switched off, a list still in flight or failed, a pool whose chat is
 * there, answers false so a healthy pool never pays for a poll.
 */
export function chatListPollIntervalMs(input: {
  chatEnabled: boolean;
  poolId: string;
  items: Parameters<typeof findPoolChat>[0]["items"];
}): number | false {
  if (!input.chatEnabled || input.items === null) {
    return false;
  }
  return findPoolChat({ poolId: input.poolId, items: input.items }) === null
    ? CHAT_PENDING_POLL_MS
    : false;
}

export function notificationsRowBody(input: {
  followsGlobal: boolean | null;
}): string | null {
  if (input.followsGlobal === null) {
    return null;
  }
  return input.followsGlobal
    ? NOTIFICATIONS_FOLLOWING_GLOBAL
    : NOTIFICATIONS_CUSTOM;
}

export function rulesText(preamble: string | null | undefined): string | null {
  if (preamble === null || preamble === undefined) {
    return null;
  }
  const trimmed = preamble.trim();
  return trimmed === "" ? null : trimmed;
}
